// Edit any string below — they drive the whole experience.
//
// Family situation (May 2026):
//   • Dhruv (Papa) lives in Seattle.
//   • Aastha + Aven (2.5y) have been in Delhi since January 20, 2026.
//   • So Aven is WITH Mama every day. He is NOT longing for her.
//   • The one missing the family is Papa, who built this gift.
//
// All copy is written from that point of view: present-tense affirmations
// from Aven (who lives with her), and a longing letter from Papa (who doesn't).

/* ── INTRO ── */
export const INTRO_TITLE = "For the woman who turned us into a family.";
export const INTRO_LINES = [
  "Aastha,",
  "",
  "It's been almost four months since I last held you.",
  "Aven has had you the whole time —",
  "every morning, every nap, every song.",
  "",
  "I've had video calls,",
  "and the sound of him laughing in your arms.",
  "",
  "This Mother's Day, you are the harder half of our family —",
  "holding our whole world together in Delhi",
  "while one of us waits in Seattle.",
  "",
  "So I built you a little corner of the internet.",
  "A bouquet, a memory wall, a few small games, and one long letter.",
  "",
  "Take your time. Tap everything. Skip what you want.",
  "Every petal is a thank-you.",
];
export const INTRO_CTA = "Begin ❀";

/* ── JOURNEY (cinematic photo timeline) ──
   These caption every photo in src/pages/mothersday/photoManifest.js → JOURNEY_PHOTOS.
   Length should match (14). Edit freely. */
export const JOURNEY_CAPTIONS = [
  "October. The first time he reached for you and meant it.",
  "October still. Your laugh became his first favorite sound.",
  "Same week, different angle — same hug, same forever.",
  "November. Birthday two. He blew the candles. You wished for him.",
  "January. The morning he learned the word ‘Mama’ on purpose.",
  "Valentine's Day. Pink, red, and you — three of my favorite things.",
  "March. He started telling you secrets in a language only you understand.",
  "March. He started imitating the way you fix his hair.",
  "Same day — caught mid-giggle. I keep this one in my wallet.",
  "April. Holi week. Your dupatta, his cheeks, our whole heart.",
  "April still — the one where he wouldn't let go.",
  "Late April. The yellow kurta. The hug. The photo I look at the most.",
  "April again. Same hug, his choice this time.",
  "End of April. The last photo I scroll to before I sleep.",
];
export const JOURNEY_OUTRO = "Every one of these moments — you made.";

/* ── BOUQUET (mini-game) ── */
export const FLOWER_PROMPTS = [
  { id: 1, button: "Tap for Mama's hugs 🤗", note: "Mama, your hugs make my world feel safe. — Aven", color: "#ff6b9d" },
  { id: 2, button: "Tap for Mama's laugh", note: "When you laugh, the whole house laughs with you.", color: "#ffa07a" },
  { id: 3, button: "Tap for Mama's cooking", note: "Your dal makes everything okay. Even the bad days.", color: "#ffd93d" },
  { id: 4, button: "Tap for Mama's bedtime stories", note: "Your voice is the softest place in the world.", color: "#c44569" },
  { id: 5, button: "Tap for Mama's patience", note: "You teach me kindness without ever raising your voice.", color: "#9b59b6" },
  { id: 6, button: "Tap for Mama's strength", note: "You hold our whole family up — and make it look easy.", color: "#e74c3c" },
  { id: 7, button: "Tap for Mama's love", note: "And for everything else words can't reach.", color: "#ff1493" },
];
export const BOUQUET_HEADER = "A Bouquet From Aven";
export const BOUQUET_SUB = "tap each flower to plant it";
export const BOUQUET_DONE = "Bouquet complete. Aven says: ‘for Mama. all of it.’";

/* ── CAPTION THE MEMORY (Lvl 3) ──
   One photo at a time. Three captions below. One is what Aven would
   actually say to Mama (he's WITH her in Delhi, present-tense), two are
   gentle toddler decoys. Picking the right one reveals his "voice" as a
   bubble over the photo and advances the round.
   Length should equal MATCH_PHOTOS in photoManifest.js (6).
*/
export const MATCH_HEADER = "What is Aven saying?";
export const MATCH_SUB = "Tap the line that sounds most like him.";
export const MATCH_ROUNDS = [
  {
    correct: "‘Mama, you smell like home.’",
    decoys: [
      "‘Mama, can I have one more biscuit?’",
      "‘Mama, the moon is following us.’",
    ],
    afterglow: "He buries his face in your dupatta every single morning.",
  },
  {
    correct: "‘When you laugh, I copy you. Every time.’",
    decoys: [
      "‘I want to wear Nana's shoes today.’",
      "‘Mama, the cat is in the garden.’",
    ],
    afterglow: "Your laugh is the first sound he learned to imitate.",
  },
  {
    correct: "‘Your hands fix everything, Mama.’",
    decoys: [
      "‘I made a tower with the spoons.’",
      "‘Why is that uncle so tall?’",
    ],
    afterglow: "Skinned knees, broken toys, bad dreams — your hands, every time.",
  },
  {
    correct: "‘Mama, can we call Papa?’",
    decoys: [
      "‘Can I press the elevator button?’",
      "‘There is a bird in the window.’",
    ],
    afterglow: "Every evening after dinner, he holds your phone and waits for me.",
  },
  {
    correct: "‘No one sings to me like you do.’",
    decoys: [
      "‘I want the blue cup, not the red one.’",
      "‘Why is the floor wet, Mama?’",
    ],
    afterglow: "He still hums your lullaby in his sleep.",
  },
  {
    correct: "‘Mama, hold me till I sleep.’",
    decoys: [
      "‘Nana let me eat ice cream for breakfast.’",
      "‘The sky is pink today.’",
    ],
    afterglow: "He won't close his eyes unless your hand is on his back.",
  },
];

/* ── SCRATCH TO REVEAL ──
   5 cards. Each has a teaser (shown on the scratch layer), the reveal
   caption (after scratching), and references a photo index from GALLERY_PHOTOS.
*/
export const SCRATCH_CARDS = [
  {
    photoIndex: 0,
    tease: "Under this sparkle is the day he wouldn't let go...",
    reveal: "'Mama, I hold you so tight because I never want to forget how safe you feel.'",
  },
  {
    photoIndex: 5,
    tease: "Scratch to find the hug that started it all...",
    reveal: "'This was the first time I understood what love means.'",
  },
  {
    photoIndex: 9,
    tease: "Hidden here is a kiss worth a thousand words...",
    reveal: "'Every kiss from you, Mama, makes the whole world quiet.'",
  },
  {
    photoIndex: 3,
    tease: "Beneath this glow is a moment of pure love...",
    reveal: "'When you hold me like this, Mama, I feel like nothing can ever go wrong.'",
  },
  {
    photoIndex: 43,
    tease: "One last scratch — this one's my favorite...",
    reveal: "'Mama, you make every ordinary day feel like magic.'",
  },
];
export const SCRATCH_HEADER = "Scratch to Reveal";
export const SCRATCH_SUB = "Use your finger to uncover hidden memories";

/* ── MEMORY WALL ── */
export const WALL_HEADER = "The Memory Wall";
export const WALL_SUB = "A year, give or take. Tap any photo to make it bigger.";

/* ── FINALE ── */
export const FINALE_TITLE = "A letter, finally.";
export const FINALE_LETTER = [
  "To the woman who turned a house into our home,",
  "who turned a baby into a little person,",
  "who turned every ordinary Tuesday into a memory —",
  "",
  "You've spent the last four months being mother and son's whole world,",
  "in a city away from your own, in a house full of suitcases,",
  "holding our boy together while half of his family is in Seattle.",
  "",
  "Aven tells me everything, in his way:",
  "that you sing him to sleep,",
  "that you fix everything with your hands,",
  "that he laughs because you laughed first.",
  "",
  "I built this from Seattle. Every photo here",
  "was one I went hunting for at midnight,",
  "remembering it the way only a husband remembers his wife.",
  "",
  "You held the whole family up while one of us is here",
  "and the other is at the airport in a way only husbands do —",
  "checking flight statuses for no reason, just to feel close to you.",
  "",
  "Come home soon. Both of you.",
  "I'll have dal on the stove, the lights on,",
  "and the quietest, most patient version of me",
  "at the door, with the silliest plan to never let either of you go again.",
];
export const FINALE_SIGNOFF = "Happy Mother's Day, Aastha.";
export const FINALE_FROM = "— Dhruv · Seattle, waiting · May 2026";

/* ── ACT META ── */
export const ACTS = [
  { id: "intro",   label: "Open" },
  { id: "catch",   label: "Lvl 1 · Catch" },
  { id: "puzzle",  label: "Lvl 2 · Puzzle" },
  { id: "match",   label: "Lvl 3 · Match" },
  { id: "boss",    label: "Boss · Reach" },
  { id: "journey", label: "Reward · Year" },
  { id: "bouquet", label: "Bouquet" },
  { id: "scratch", label: "Scratch" },
  { id: "wall",    label: "Wall" },
  { id: "finale",  label: "Letter" },
];
